import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  UploadCloud,
  Trash2,
  Pencil,
  LogOut,
  Check,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import PortalGate from "@/components/PortalGate";
import { portalApi, portalAuth, API } from "@/lib/portal";

const CATEGORIES = ["Professional Service Projects Capability Building"];

const initialForm = {
  category: CATEGORIES[0],
  title: "",
  description: "",
  file: null,
};

const formatBytes = (bytes) => {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(0)} MB`;
};

const PortalAdmin = () => {
  const [authed, setAuthed] = useState(portalAuth.isAuthed() && portalAuth.isAdmin());
  const [form, setForm] = useState(initialForm);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videos, setVideos] = useState([]);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  const loadVideos = async () => {
    try {
      const { data } = await portalApi.get("/portal/videos");
      setVideos(data || []);
    } catch (err) {
      if (err?.response?.status === 401) setAuthed(false);
      else toast.error("Could not load videos.");
    }
  };

  useEffect(() => {
    if (authed) loadVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleSignOut = () => {
    portalAuth.clear();
    setAuthed(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file) {
      toast.error("Please choose a video file.");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    const fd = new FormData();
    fd.append("category", form.category);
    fd.append("title", form.title.trim());
    if (form.description.trim()) fd.append("description", form.description.trim());
    fd.append("file", form.file);

    setUploading(true);
    setProgress(0);
    try {
      await portalApi.post("/portal/admin/videos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });
      toast.success("Video uploaded successfully.");
      setForm(initialForm);
      if (fileRef.current) fileRef.current.value = "";
      loadVideos();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed";
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Delete "${video.title}"? This cannot be undone.`)) return;
    try {
      await portalApi.delete(`/portal/admin/videos/${video.id}`);
      toast.success("Video deleted.");
      loadVideos();
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    try {
      await portalApi.put(`/portal/admin/videos/${editing.id}`, {
        title: editing.title,
        category: editing.category,
        description: editing.description || null,
      });
      toast.success("Updated.");
      setEditing(null);
      loadVideos();
    } catch (err) {
      toast.error("Update failed.");
    }
  };

  if (!authed) {
    return <PortalGate mode="admin" onAuthed={() => setAuthed(portalAuth.isAdmin())} />;
  }

  return (
    <div data-testid="portal-admin-page">
      <section className="bg-[#111111] text-white pt-32 pb-12 lg:pt-40">
        <div className="container-pro flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="overline mb-4">Admin Console</p>
            <h1 className="font-display text-4xl lg:text-6xl text-white leading-[1.05]">
              Manage capability building
              <br />
              <span className="text-[#8B0000]">videos.</span>
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/portal"
              data-testid="admin-back-to-portal"
              className="btn-ghost-light"
            >
              <ArrowLeft size={16} /> View Portal
            </Link>
            <button
              data-testid="admin-signout-button"
              onClick={handleSignOut}
              className="btn-ghost-light"
            >
              Sign out <LogOut size={16} />
            </button>
          </div>
        </div>
      </section>

      <section className="section bg-[#F5F5DC]">
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* UPLOAD FORM */}
          <div className="lg:col-span-5">
            <form
              data-testid="admin-upload-form"
              onSubmit={handleUpload}
              className="bg-white p-8 lg:p-10 border border-[#E5E5E5] sticky top-28"
            >
              <div className="w-12 h-12 bg-[#8B0000]/10 flex items-center justify-center mb-6">
                <UploadCloud size={22} className="text-[#8B0000]" />
              </div>
              <h2 className="font-display text-2xl text-[#111111] mb-6">
                Upload a new video
              </h2>

              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#111111] mb-2 block">
                Module / Category
              </label>
              <select
                data-testid="admin-category-select"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-[#E5E5E5] px-4 py-3 bg-white text-[#111111] mb-5 focus:border-[#8B0000]"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>

              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#111111] mb-2 block">
                Title <span className="text-[#8B0000]">*</span>
              </label>
              <input
                data-testid="admin-title-input"
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. SAP S/4HANA Architecture Overview"
                className="w-full border border-[#E5E5E5] px-4 py-3 bg-white text-[#111111] mb-5 focus:border-[#8B0000]"
                required
              />

              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#111111] mb-2 block">
                Description (optional)
              </label>
              <textarea
                data-testid="admin-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Brief description of the session"
                className="w-full border border-[#E5E5E5] px-4 py-3 bg-white text-[#111111] mb-5 focus:border-[#8B0000] resize-none"
              />

              <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#111111] mb-2 block">
                MP4 file <span className="text-[#8B0000]">*</span>
              </label>
              <input
                data-testid="admin-file-input"
                ref={fileRef}
                type="file"
                accept="video/mp4,video/*"
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="w-full border border-[#E5E5E5] px-4 py-3 bg-white text-[#4A4A4A] mb-6 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#8B0000] file:text-white file:uppercase file:text-xs file:tracking-wider file:cursor-pointer"
              />
              <p className="text-xs text-[#4A4A4A] mb-6">
                Up to 500 MB. MP4 recommended for best compatibility.
              </p>

              {uploading && (
                <div className="mb-5">
                  <div className="h-1.5 bg-[#E5E5E5] overflow-hidden">
                    <div
                      data-testid="upload-progress-bar"
                      className="h-full bg-[#8B0000] transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-[#4A4A4A] mt-2">
                    Uploading… {progress}%
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={uploading}
                data-testid="admin-upload-button"
                className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {uploading ? "Uploading…" : "Upload video"}{" "}
                {!uploading && <UploadCloud size={16} />}
              </button>
            </form>
          </div>

          {/* VIDEO LIST */}
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl text-[#111111]">
                Library
                <span className="text-[#8B0000] ml-2">({videos.length})</span>
              </h2>
            </div>

            {videos.length === 0 ? (
              <div className="bg-white border border-dashed border-[#8B0000]/30 px-8 py-16 text-center">
                <p className="font-display text-xl text-[#111111] mb-2">
                  No videos yet
                </p>
                <p className="text-sm text-[#4A4A4A]">
                  Upload your first capability building session to get started.
                </p>
              </div>
            ) : (
              <ul className="space-y-3" data-testid="admin-video-list">
                {videos.map((v) => {
                  const isEditing = editing?.id === v.id;
                  return (
                    <li
                      key={v.id}
                      data-testid={`admin-video-row-${v.id}`}
                      className="bg-white border border-[#E5E5E5] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="space-y-2">
                            <select
                              value={editing.category}
                              onChange={(e) =>
                                setEditing({ ...editing, category: e.target.value })
                              }
                              className="w-full border border-[#E5E5E5] px-3 py-2 text-sm"
                            >
                              {CATEGORIES.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                            <input
                              value={editing.title}
                              onChange={(e) =>
                                setEditing({ ...editing, title: e.target.value })
                              }
                              className="w-full border border-[#E5E5E5] px-3 py-2 text-sm"
                            />
                            <textarea
                              value={editing.description || ""}
                              onChange={(e) =>
                                setEditing({ ...editing, description: e.target.value })
                              }
                              rows={2}
                              placeholder="Description"
                              className="w-full border border-[#E5E5E5] px-3 py-2 text-sm resize-none"
                            />
                          </div>
                        ) : (
                          <>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8B0000] mb-1 truncate">
                              {v.category}
                            </p>
                            <p className="font-display text-lg text-[#111111] truncate">
                              {v.title}
                            </p>
                            {v.description && (
                              <p className="text-sm text-[#4A4A4A] line-clamp-1 mt-1">
                                {v.description}
                              </p>
                            )}
                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A]/70 mt-2">
                              {formatBytes(v.size_bytes)} · {v.mime_type}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isEditing ? (
                          <>
                            <button
                              data-testid={`admin-save-${v.id}`}
                              onClick={handleSaveEdit}
                              className="w-9 h-9 border border-[#8B0000] bg-[#8B0000] text-white flex items-center justify-center hover:bg-[#660000]"
                              aria-label="Save"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              data-testid={`admin-cancel-${v.id}`}
                              onClick={() => setEditing(null)}
                              className="w-9 h-9 border border-[#E5E5E5] text-[#111111] flex items-center justify-center hover:bg-[#F5F5DC]"
                              aria-label="Cancel"
                            >
                              <XIcon size={16} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              data-testid={`admin-edit-${v.id}`}
                              onClick={() => setEditing(v)}
                              className="w-9 h-9 border border-[#E5E5E5] text-[#111111] flex items-center justify-center hover:bg-[#F5F5DC]"
                              aria-label="Edit"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              data-testid={`admin-delete-${v.id}`}
                              onClick={() => handleDelete(v)}
                              className="w-9 h-9 border border-[#E5E5E5] text-[#8B0000] flex items-center justify-center hover:bg-[#8B0000] hover:text-white hover:border-[#8B0000]"
                              aria-label="Delete"
                            >
                              <Trash2 size={15} />
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PortalAdmin;
